import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Plus, Edit, Trash2, ShoppingCart, Download, Zap, Target } from 'lucide-react';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';

const MealPlanner: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  const mealPlan = {
    '2024-01-15': {
      breakfast: { name: 'Avocado Toast with Eggs', calories: 420, protein: 18 },
      lunch: { name: 'Quinoa Buddha Bowl', calories: 650, protein: 22 },
      dinner: { name: 'Grilled Salmon with Vegetables', calories: 580, protein: 35 },
      snack: { name: 'Greek Yogurt with Berries', calories: 180, protein: 15 }
    },
    '2024-01-16': {
      breakfast: { name: 'Protein Smoothie Bowl', calories: 380, protein: 25 },
      lunch: { name: 'Mediterranean Wrap', calories: 520, protein: 20 },
      dinner: { name: 'Chicken Stir Fry', calories: 620, protein: 40 },
      snack: { name: 'Almonds and Apple', calories: 200, protein: 6 }
    }
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];

  const nutritionTargets = {
    calories: 2000,
    protein: 150,
    carbs: 250,
    fat: 67
  };

  const shoppingList = [
    { category: 'Proteins', items: ['Salmon fillets', 'Chicken breast', 'Greek yogurt', 'Eggs'] },
    { category: 'Vegetables', items: ['Avocado', 'Spinach', 'Bell peppers', 'Broccoli'] },
    { category: 'Grains', items: ['Quinoa', 'Whole wheat bread', 'Brown rice'] },
    { category: 'Fruits', items: ['Berries', 'Bananas', 'Apples'] },
    { category: 'Pantry', items: ['Olive oil', 'Almonds', 'Chia seeds'] }
  ];

  const generateMealPlan = () => {
    // AI meal plan generation logic would go here
    console.log('Generating AI meal plan...');
  };

  const exportMealPlan = () => {
    // Export functionality
    console.log('Exporting meal plan...');
  };

  const getDayCalories = (date: string) => {
    const meals = mealPlan[date];
    if (!meals) return 0;
    return Object.values(meals).reduce((total, meal) => total + meal.calories, 0);
  };

  const getDayProtein = (date: string) => {
    const meals = mealPlan[date];
    if (!meals) return 0;
    return Object.values(meals).reduce((total, meal) => total + meal.protein, 0);
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
              <Calendar className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            AI Meal <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Planner
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Intelligent meal planning with AI-powered optimization, automated shopping lists, 
            and personalized nutrition targeting for your health goals.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8"
        >
          <GlassCard className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => setViewMode('week')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      viewMode === 'week'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : 'bg-white/10 text-purple-200 hover:bg-white/20'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setViewMode('month')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      viewMode === 'month'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : 'bg-white/10 text-purple-200 hover:bg-white/20'
                    }`}
                  >
                    Month
                  </button>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <motion.button
                  onClick={generateMealPlan}
                  className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Zap className="w-4 h-4" />
                  <span>Generate AI Plan</span>
                </motion.button>
                
                <button
                  onClick={exportMealPlan}
                  className="flex items-center space-x-2 bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Meal Calendar */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <Calendar className="w-6 h-6 text-purple-400 mr-2" />
                  Weekly Meal Plan
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left text-purple-300 font-medium p-3">Meal</th>
                        {weekDays.map((day) => (
                          <th key={day} className="text-center text-purple-300 font-medium p-3 min-w-32">
                            {day}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mealTypes.map((mealType) => (
                        <tr key={mealType} className="border-t border-white/10">
                          <td className="p-3">
                            <span className="text-white font-medium capitalize">{mealType}</span>
                          </td>
                          {weekDays.map((day, dayIndex) => {
                            const date = new Date();
                            date.setDate(date.getDate() - date.getDay() + dayIndex + 1);
                            const dateStr = date.toISOString().split('T')[0];
                            const meal = mealPlan[dateStr]?.[mealType as keyof typeof mealPlan[string]];
                            
                            return (
                              <td key={day} className="p-3">
                                {meal ? (
                                  <div className="bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer group">
                                    <div className="text-white text-sm font-medium mb-1">{meal.name}</div>
                                    <div className="text-purple-300 text-xs">{meal.calories} cal</div>
                                    <div className="text-purple-300 text-xs">{meal.protein}g protein</div>
                                    <div className="flex space-x-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button className="p-1 text-purple-400 hover:text-purple-300">
                                        <Edit className="w-3 h-3" />
                                      </button>
                                      <button className="p-1 text-red-400 hover:text-red-300">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button className="w-full h-20 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center text-purple-400 hover:border-purple-400 hover:text-purple-300 transition-colors">
                                    <Plus className="w-5 h-5" />
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Daily Nutrition Summary */}
                <div className="mt-6 grid grid-cols-7 gap-2">
                  {weekDays.map((day, dayIndex) => {
                    const date = new Date();
                    date.setDate(date.getDate() - date.getDay() + dayIndex + 1);
                    const dateStr = date.toISOString().split('T')[0];
                    const calories = getDayCalories(dateStr);
                    const protein = getDayProtein(dateStr);
                    
                    return (
                      <div key={day} className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-white text-sm font-medium mb-1">{day}</div>
                        <div className="text-purple-300 text-xs">{calories} cal</div>
                        <div className="text-purple-300 text-xs">{protein}g protein</div>
                        <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                          <div
                            className="h-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                            style={{ width: `${Math.min((calories / nutritionTargets.calories) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Nutrition Targets */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <GlassCard className="p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Target className="w-5 h-5 text-purple-400 mr-2" />
                  Daily Targets
                </h3>
                <div className="space-y-4">
                  {Object.entries(nutritionTargets).map(([nutrient, target]) => (
                    <div key={nutrient} className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-white capitalize">{nutrient}</span>
                        <span className="text-purple-300">{target}{nutrient === 'calories' ? '' : 'g'}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>

            {/* Shopping List */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <ShoppingCart className="w-5 h-5 text-cyan-400 mr-2" />
                    Shopping List
                  </h3>
                  <button className="text-cyan-400 hover:text-cyan-300 text-sm">
                    Generate
                  </button>
                </div>
                <div className="space-y-4">
                  {shoppingList.map((category) => (
                    <div key={category.category}>
                      <h4 className="text-purple-300 font-medium mb-2">{category.category}</h4>
                      <div className="space-y-1">
                        {category.items.map((item) => (
                          <label key={item} className="flex items-center space-x-2 text-sm">
                            <input type="checkbox" className="rounded border-white/20 bg-white/10" />
                            <span className="text-white">{item}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealPlanner;